import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { secureGet, secureDelete } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { PrimaryButton } from '../../components/PrimaryButton';
import GenericCard from '../Users/GenericCard';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Filter from '../../components/Filter';
import OptionsMenu from '../../components/OptionsMenu';
import ModuleWrapper from '../../components/ModuleWrapper';
import SnackbarNotification from '../../components/SnackbarNotification';
import TableLoadingArea from '../../components/TableLoadingArea';

import AddIcon from '../../icons/plusB.svg';
import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';

const Insurers = () => {
  const [insurers, setInsurers] = useState([]);
  const [filteredInsurers, setFilteredInsurers] = useState([]);
  const [currentPageInsurers, setCurrentPageInsurers] = useState([]);
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [filters, setFilters] = useState({ needle: '' });
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [selectedInsurer, setSelectedInsurer] = useState(null);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const topRef = useRef(null);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const filterOptions = [
    { name: 'needle', type: 'search' },
  ];

  const paginateData = (data, page, size) => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    return data.slice(startIndex, endIndex);
  };

  const updatePagination = useCallback((data, page, size) => {
    setTotalPages(Math.ceil((data?.length || 0) / size) || 1);
    setTotalElements(data?.length || 0);
    setCurrentPageInsurers(paginateData(data || [], page, size));
  }, []);

  const fetchInsurers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureGet('/insurance-companies', showSnackbar, { signal });
      const list = res?.data || [];
      console.log("list", list);
      setInsurers(list);
      setFilteredInsurers(list);
      setCurrentPage(0);
      updatePagination(list, 0, pageSize);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching insurers:', err);
    } finally {
      setLoading(false);
    }
  }, [signal, pageSize, updatePagination]);

  useEffect(() => {
    const ac = new AbortController();
    fetchInsurers();
    return () => ac.abort();
  }, [fetchInsurers]);

  const applyFilters = useCallback(() => {
    let filtered = [...insurers];

    if (filters.needle) {
      const q = filters.needle.toLowerCase();
      filtered = filtered.filter((b) =>
        (b.name || '').toLowerCase().includes(q)
      );
    }

    setFilteredInsurers(filtered);
    setCurrentPage(0);
    updatePagination(filtered, 0, pageSize);
  }, [insurers, filters, pageSize, updatePagination]);

  useEffect(() => {
    applyFilters();
  }, [filters, insurers, applyFilters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setCurrentPageInsurers(paginateData(filteredInsurers, page, pageSize));
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    updatePagination(filteredInsurers, 0, newPageSize);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ needle: '' });
    setFilteredInsurers(insurers);
    updatePagination(insurers, 0, pageSize);
  };

  const handleAddClick = () => {
    setSelectedInsurer(null);
    setShowModule(true);
  };

  const handleEditClick = (insurer) => {
    setSelectedInsurer(insurer);
    setShowModule(true);
  };

  const handleDeleteClick = (insurer) => {
    setItemToDelete(insurer);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/insurance-companies/${itemToDelete.id}`, showSnackbar);
      await fetchInsurers();
      showSnackbar(t('confirmation.insurersDelete') || 'Assureur supprimé', 'success');
    } catch (e) {
      console.error('Error deleting insurer:', e);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleModuleClose = (action = 'cancel') => {
    if (action === 'submit') fetchInsurers();
    setShowModule(false);
    setSelectedInsurer(null);
  };

  const getMenuOptions = (insurer) => ([
    { label: t('actions.modify'), icon: EditIcon, action: () => handleEditClick(insurer) },
    { label: t('actions.delete'), icon: DeleteIcon, color: '#EF476F', action: () => handleDeleteClick(insurer) },
  ]);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t('titles.insurers') || 'Gestion des assureurs'}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t('titles.insurers') || 'Gestion des assureurs'}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t('primaryButtons.add_insurer') || 'Ajouter un assureur'}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading} minHeight={200} className="mt-6">
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && currentPageInsurers.map((insurer) => (
            <GenericCard
              key={insurer.id}
              title={insurer.name || ''}
              subtitle={insurer.code || ''}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  <OptionsMenu options={getMenuOptions(insurer)} />
                </div>
              }
            />
          ))}
        </div>

        {!loading && currentPageInsurers.length === 0 && (
          <div className="text-center mt-4">
          </div>
        )}
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="insurance-companies"
          mode={selectedInsurer ? 'modify' : 'add'}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedInsurer || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Insurers;
